import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  ModalContainer,
  ModalHeader,
  TextInputWrapper,
  AttachFilesWrapper,
  Footer,
  SliderContainer,
  PromptAndMediaWrapper,
  TokenAvailabilityContainer,
  TokensContainer,
  Separator,
  UpgradeText,
  TokensHeadingContainer,
  ButtonContainer,
  StyledDialogContent,
  StyledDialogTitle,
  IconWrapper,
  Content,
  DialogButtonContainer,
  StyledDialog,
} from "./NewAIBuilder.styled";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { Modal, Dialog } from "@mui/material";
import TextView from "../TextView";
import CustomAISlider from "components/AIBuilderSlider";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getUserProfilePath, handleFileUpload } from "../../utils/fileUpload";
import { ASSETS } from "../../utils/config";
import Loader from "../CustomLoader";
import { generateAiWheelAction } from "../../redux/actions";
import { generateAiWheel } from "../../services/api";
import { hideCreateWheelPopup } from "../../redux/reducers/createWheelPopup";
import { getLoggedInUser, getRandomId } from "../../utils/helpers";
import AiBuilderSuccessModal from "./Sections/Success";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import { FEATURES } from "../../constants/features";
import FeatureEligibility from "../FeatureEligibility";
import { colors } from "../../utils";
import Button from "../Button";
import { addAiWheel, clearSelectedBundle } from "../../redux/reducers/aiWheels";
import { useDispatch, useSelector } from "react-redux";
import useTokenCalculator from "../../hooks/useTokenCalculator";
import InfoIcon from "assets/images/createWheel/info.svg";
import TokensIcon from "assets/images/tokens/tokens.svg";
import {
  openDialogPopup,
  closeDialogPopup,
} from "../../redux/reducers/dialogPopup";
import { TokenUpgradeContent } from "./Sections/Tokens";
import { useAIFeatureEligibility } from "../../hooks/useAIFeatureEligibility";
import premiumPackageIcon from "@assets/images/packages/premiumPackageIcon.svg";
import { StyledTooltip } from "../IyugaTooltip";
import { useNavigate } from "react-router-dom";
import { getActiveSubscriptionData } from "../../redux/selectors/subscriptionSelectors";
import { openUpgradePopup } from "../../redux/reducers/upgradePopup";
import { purchaseTokenBundle } from "../../services/api";
import { toast } from "react-toastify";
import { useStore } from "react-redux";
import { SubscriptionPage } from "../Settings/Subscription/index";
import { ConfirmPurchase } from "./Sections/Tokens/confirmPurchase";
import CheckCircle from "assets/images/tokens/checkCircle.svg";
import IyugaTooltip from "../IyugaTooltip";

function NewAIBuilder({ isOpen, onClose, onSuccess, onCancelWizard }) {
  const navigate = useNavigate();
  const store = useStore();
  const pasteRef = useRef(null);

  // const fileInputRef = useRef(null);

  const [characterLimit] = useState(100000); // Max characters allowed in prompt
  const [wordCount, setWordCount] = useState(500); // Words per generated segment
  const [segmentCount, setSegmentCount] = useState(4);
  const [variation, setVariation] = useState(0.1);
  const [textareaContent, setTextareaContent] = useState("");
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false); // Toggle advanced settings
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of selected files
  const [loading, setLoading] = useState(false); // Array of uploaded file URLs
  const [aiWheelCreated, setAiWheelCreated] = useState(false); // Array of uploaded file URLs
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  // const { isEligible, isCheckingEligibility, subscription,requiredSubscription } = useIfUserEligible(
  //   FEATURES.AI_MEDIA_UPLOADS
  // );

  const { eligibility, refetch } = useAIFeatureEligibility();

  const { data: currentPlan, loading: currentPlanLoading } = useSelector(
    getActiveSubscriptionData
  );

  const availableTokens = eligibility.wheel.remainingTokens;

  const { tokenCount, isWithinLimit, AISegmentTokens, AImediaTokens } =
    useTokenCalculator(
      textareaContent,
      selectedFiles,
      availableTokens,
      wordCount,
      segmentCount
    );

  const dispatch = useDispatch();

  const selectedBundle = useSelector((state) => state.aiWheels.selectedBundle);

  const buyTokenBundle = async () => {
    const state = store.getState();
    const id = state.aiWheels.selectedBundle?.id;

    const response = await purchaseTokenBundle({ bundle_id: id });
    if (response?.status === 200) {
      toast.success("Token bundle purchased successfully.");
      dispatch(closeDialogPopup());
      closePurchaseDialog();

      await refetch();
    } else if (response?.data?.message.includes("insufficient funds")) {
      toast.error("Your card has insufficient funds");
    } else if (response?.status === 400) {
      toast.error("Please add a payment method.");
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  // const isEligible = false;
  // Handle file selection
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);

    e.target.value = "";
  };

  const userProfileUploadPath = getUserProfilePath({
    user_id: getLoggedInUser()?.id,
  });

  const uploadFiles = async (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      throw new Error("No files provided for upload.");
    }

    const uploadPromises = files.map((file) => {
      if (!file || !file.name || !file.type) {
        throw new Error("Invalid file object.");
      }
      return handleFileUpload(file, userProfileUploadPath);
    });

    return Promise.all(uploadPromises);
  };

  const handleUpload = async () => {
    if (!textareaContent.trim()) {
      return;
    }

    const temp_id = getRandomId();
    const media = [];
    const documents = [];
    const data = {
      prompt: textareaContent,
      segment_count: segmentCount,
      word_count: wordCount,
      variation,
      temp_id,
      media,
      documents,
      intended_tokens: tokenCount,
    };

    try {
      setLoading(true);
      if (selectedFiles.length > 0) {
        // Upload all files
        const locationResults = await uploadFiles(selectedFiles);

        // Process uploaded file results
        locationResults.forEach((result) => {
          if (!result || !result.location || !result.fileType) {
            throw new Error("Invalid upload result.");
          }

          const { location, fileType } = result;

          // Categorize based on MIME type
          if (fileType.startsWith("image/")) {
            media.push(location);
          } else {
            documents.push(location);
          }
        });
      }

      // Send data to generateAiWheel
      const response = await generateAiWheel(data);

      if (response?.status === 200) {
        // Reset form state
        setTextareaContent("");
        setSelectedFiles([]);
        setSegmentCount(4);
        setWordCount(500);
        setVariation(0.1);
        setShowAdvancedSettings(false);
        setAiWheelCreated(true);
        dispatch(addAiWheel({ id: temp_id, prompt: textareaContent }));
      }
    } catch (error) {
      console.error("Error during handleUpload:", error);
      // Here you could add toast notification or other error feedback to user
    } finally {
      setLoading(false);
    }
  };

  const handleTextareaChange = (event) => {
    const input = event.target.value;
    if (input.length <= characterLimit) {
      setTextareaContent(input);
    }
  };

  const handleInput = (type, value) => {
    switch (type) {
      case "segmentCount":
        setSegmentCount(value);
        break;
      case "wordCount":
        setWordCount(value);
        break;
      case "variation":
        setVariation(value);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    onClose();
  };
  const handlePaste = (e) => {
    console.log("Paste event triggered");

    // Prevent default behavior to ensure we handle the paste ourselves
    e.preventDefault();

    const items = e.clipboardData?.items;
    if (!items) return;

    let hasImageBeenPasted = false;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        hasImageBeenPasted = true;
        const file = item.getAsFile();
        if (file) {
          // Give the file a more descriptive name with timestamp
          const imageType = file.type.replace("image/", "");
          const renamedFile = new File(
            [file],
            `clipboard_image_${new Date()
              .toISOString()
              .replace(/[:.]/g, "-")}.${imageType}`,
            { type: file.type }
          );

          // Add directly to our selected files
          setSelectedFiles((prevFiles) => [...prevFiles, renamedFile]);
        }
      }
    }

    if (hasImageBeenPasted) {
      toast.success("Image pasted from clipboard");
    } else {
      toast.info("No image found in clipboard");
    }
  };

  const focusPasteArea = () => {
    // Request permission if needed (some browsers require activation)
    if (navigator.clipboard && typeof navigator.clipboard.read === "function") {
      navigator.clipboard
        .read()
        .then((clipboardItems) => {
          processClipboardItems(clipboardItems);
        })
        .catch((err) => {
          console.error("Failed to read clipboard: ", err);
          // Fallback to the contentEditable method
          setTimeout(() => {
            if (pasteRef.current) {
              pasteRef.current.focus();
            }
          }, 0);
        });
    } else {
      // Fallback for browsers without Clipboard API support
      setTimeout(() => {
        if (pasteRef.current) {
          pasteRef.current.focus();
        }
      }, 0);
    }
  };

  const processClipboardItems = async (clipboardItems) => {
    let hasImageBeenPasted = false;

    try {
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            hasImageBeenPasted = true;
            const blob = await item.getType(type);
            const imageType = type.replace("image/", "");
            const file = new File(
              [blob],
              `clipboard_image_${Date.now()}.${imageType}`,
              { type }
            );

            setSelectedFiles((prevFiles) => [...prevFiles, file]);
          }
        }
      }

      if (hasImageBeenPasted) {
        toast.success("Image pasted from clipboard");
      } else {
        toast.info("No image found in clipboard");
      }
    } catch (error) {
      console.error("Error processing clipboard items:", error);
      toast.error("Failed to process clipboard content");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const closePurchaseDialog = () => {
    dispatch(clearSelectedBundle());
    setOpenPurchaseDialog(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  if (aiWheelCreated) {
    return (
      <AiBuilderSuccessModal
        onSuccess={onSuccess}
        isOpen={true}
        onClose={handleClose}
      />
    );
  }

  const openPurchaseConfirmationPopup = () => {
    dispatch(
      openDialogPopup({
        isOpen: true,
        icon: CheckCircle,
        title: "Purchase Confirmation",
        primaryButtonTitle: "Confirm Purchase",
        secondaryButtonTitle: "Cancel",
        primaryButtonOnClick: buyTokenBundle,
        secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
        disableClose: true,
        extraContent: <ConfirmPurchase />,
      })
    );
  };

  const openSubscriptionPopup = () => {
    dispatch(
      openDialogPopup({
        isOpen: true,
        title: "Choose the plan to upgrade",
        secondaryButtonTitle: "Cancel",
        secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
        extraContent: <SubscriptionPage />,
        autoWidth: true,
      })
    );
  };

  // const openPurchaseModal = () => {
  //   dispatch(
  //     openDialogPopup({
  //       isOpen: true,
  //       title: "AI Token bundles",
  //       primaryButtonTitle: "Purchase",
  //       secondaryButtonTitle: "Cancel",
  //       secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
  //       extraContent: <TokenUpgradeContent />,
  //       disableClose: true,
  //       primaryButtonOnClick: openPurchaseConfirmationPopup
  //     })
  //   );

  const handlePremiumDialog = () => {
    dispatch(
      openDialogPopup({
        isOpen: true,
        title: "Upgrade to Premium",
        text: "Upgrade to Premium to attach files or paste from clipboard.",
        primaryButtonTitle: "Upgrade today!",
        secondaryButtonTitle: "Cancel",
        secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
        primaryButtonOnClick: () => {
          dispatch(closeDialogPopup());
          dispatch(
            openUpgradePopup({
              requiredSubscription: 2,
            })
          );
        },
      })
    );
  };

  return (
    <>
      <Modal open={isOpen} onClose={onCancelWizard}>
        <ModalContainer onDragOver={handleDragOver} onDrop={handleDrop}>
          {/* Header */}
          <ModalHeader>
            <TextView variant="title">
              <img
                src={ASSETS.NewAiIcon}
                alt="ai"
                style={{ height: "30px", width: "30px", marginRight: "10px" }}
              />
              AI Wheel Builder
            </TextView>
            <CloseIcon className="close-icon" onClick={onCancelWizard} />
          </ModalHeader>
          <TextView variant="subtitle" style={{ marginBottom: "10px" }}>
            Share what you'd like on your wheel below!
          </TextView>

          {/* Advanced Settings Button */}
          <button
            style={{
              display: "flex",
              alignItems: "center",
              background: "transparent",
              border: "none",
              color: "#007bff",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
              padding: "-10px",
              marginBottom: "10px",
              marginLeft: "-10px",
            }}
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          >
            <ArrowDropDownIcon
              style={{
                transition: "transform 0.3s ease",
                transform: showAdvancedSettings
                  ? "rotate(360deg)"
                  : "rotate(270deg)",
                color: "#BBBBBB",
              }}
            />
            <TextView style={{ marginLeft: "1px", color: "#0F52BA" }}>
              Advance Settings
            </TextView>
          </button>

          {/* Conditionally Render Advanced Settings */}
          {showAdvancedSettings && (
            <SliderContainer>
              <CustomAISlider
                label="Segment Count"
                min={4}
                max={10}
                value={segmentCount}
                steps={1}
                filledColor="#F79A00"
                thumbColor="#F79A00"
                bgColor="#C283DA"
                onChange={(e, val) => handleInput("segmentCount", val)}
              />
              <CustomAISlider
                label="Word Count"
                min={500}
                max={1000}
                value={wordCount}
                steps={100}
                onChange={(e, val) => handleInput("wordCount", val)}
                infoText="Words per segment"
              />
              <CustomAISlider
                label="Variation"
                min={0.0}
                max={1.0}
                value={variation}
                steps={0.2}
                onChange={(e, val) => handleInput("variation", val)}
                infoText="The higher the Variation, the more creative freedom the AI Generator will take with your prompt"
              />
            </SliderContainer>
          )}

          <PromptAndMediaWrapper>
            {/* Text Input */}
            <TextInputWrapper>
              <textarea
                value={textareaContent}
                onChange={handleTextareaChange}
                placeholder="Type here"
              ></textarea>
              {/* Word counter */}
              <TextView
                variant="text"
                style={{
                  alignSelf: "flex-end",
                  marginTop: "5px",
                  fontWeight: 600,
                  color:
                    textareaContent.length >= characterLimit
                      ? "#ED4337"
                      : "#757575",
                }}
              >
                {textareaContent.length}/{characterLimit} characters
              </TextView>
            </TextInputWrapper>

            <div
              style={
                !eligibility.media.isEligible
                  ? {
                      backgroundColor: colors.PrimaryPurple100,
                      padding: 20,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }
                  : {
                      padding: "12px",
                    }
              }
            >
              <div
                ref={pasteRef}
                contentEditable
                onPaste={handlePaste}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 1,
                  width: 1,
                  overflow: "hidden",
                }}
              />
              <AttachFilesWrapper
                style={
                  !eligibility.media.isEligible
                    ? { justifyContent: "center" }
                    : {}
                }
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelection}
                  style={{ display: "none" }}
                  id="file-input"
                />
                <Button
                  disabled={!eligibility.media.isEligible}
                  onClick={() => document.getElementById("file-input").click()}
                  style={
                    eligibility.media.isEligible
                      ? {
                          backgroundColor: colors.background,
                          color: colors.darkGrey,
                        }
                      : {}
                  }
                >
                  <img
                    src={ASSETS.LinksIcon_hotbar}
                    style={{ marginRight: "5px", height: 14 }}
                    alt="icon"
                  />
                  Attach Images
                </Button>
                <TextView
                  variant="text"
                  color="#757575"
                  style={{ fontWeight: "600" }}
                >
                  OR
                </TextView>
                <Button
                  style={
                    eligibility.media.isEligible
                      ? {
                          backgroundColor: colors.background,
                          color: colors.darkGrey,
                        }
                      : {}
                  }
                  disabled={!eligibility.media.isEligible}
                  onClick={focusPasteArea}
                >
                  <img
                    src={ASSETS.NewDoc}
                    style={{ marginRight: "5px" }}
                    alt="icon"
                  />
                  Paste from Clipboard
                </Button>
                {(currentPlan?.sub_tier === 0 ||
                  currentPlan?.sub_tier === 1) && (
                  <StyledTooltip
                    title={"Premium Tier feature"}
                    arrow
                    placement="bottom"
                    color={colors.PrimaryPurple}
                    textColor={"#fff"}
                  >
                    <img
                      src={premiumPackageIcon}
                      alt="premium"
                      width={24}
                      height={24}
                      onClick={handlePremiumDialog}
                      style={{ cursor: "pointer" }}
                    />
                  </StyledTooltip>
                )}
              </AttachFilesWrapper>
            </div>
          </PromptAndMediaWrapper>

          {!eligibility.media.isEligible && (
            <FeatureEligibility
              subscription={eligibility.media.subscription}
              requiredSubscription={eligibility.media.requiredSubscription}
              style={{
                marginTop: 0,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              }}
            />
          )}

          {selectedFiles.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <TextView variant="subtitle">Selected Files:</TextView>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "10px",
                  justifyContent: "flex-start",
                  marginBottom: 10,
                }}
              >
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "2px",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#f7f7f7",
                      padding: "10px",
                      borderRadius: "8px",
                      position: "relative",
                      width: "160px", // Adjust width for better fit
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    <TextView variant="text" color="#000">
                      {file.name}
                    </TextView>
                    {file.type.startsWith("image") && (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "5px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {/* Close Icon */}
                    <CloseIcon
                      onClick={() => {
                        setSelectedFiles((prevFiles) =>
                          prevFiles.filter((_, i) => i !== index)
                        );
                      }}
                      style={{
                        display: "flex",
                        alignSelf: "baseline",
                        cursor: "pointer",
                        color: "#BBBBBB",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <TokenAvailabilityContainer style={{ marginTop: 18 }}>
            <TokensContainer>
              <TokensHeadingContainer>
                <img src={TokensIcon} alt="tokens" width={20} height={20} />
                <TextView variant="subtitle">AI Tokens</TextView>
                <IyugaTooltip
                  segmentCount
                  tooltip={
                    <div
                      style={{
                        whiteSpace: "pre-line",
                      }}
                    >
                      {`Segment Count, Words per Segment, Prompt Length, and each Media Upload contributes to the total token usage`}
                      {textareaContent.trim() && (
                        <>
                          {`\n\n`}
                          {`Words per Segment = ${wordCount}\n`}
                          {`Segment Count = ${segmentCount}\n`}
                          <b>{`Total Segment Token Cost = ${AISegmentTokens}`}</b>
                          {`\n\n`}
                          {`Prompt Length = ${
                            textareaContent?.length || 0
                          } characters\n`}
                          {`Media Uploads = ${selectedFiles?.length || 0}\n`}
                          <b>{`Total Prompt/Media Token Cost = ${AImediaTokens} tokens`}</b>
                          {`\n\n`}
                          <b>{`Total Token Usage = ${tokenCount} tokens`}</b>
                        </>
                      )}
                    </div>
                  }
                  width={"350px"}
                >
                  <img src={InfoIcon} alt="info" width={20} height={20} />
                </IyugaTooltip>
              </TokensHeadingContainer>

              <TokensHeadingContainer>
                <TextView variant="text" color="#757575">
                  Available:
                  <span
                    style={{
                      color: "#0F0F0F",
                      fontWeight: 600,
                      marginLeft: "4px",
                    }}
                  >
                    {availableTokens}
                  </span>
                </TextView>

                <Separator />

                <TextView variant="text" color="#757575">
                  Requires:
                  <span
                    style={{
                      color:
                        tokenCount > availableTokens ? "#ED4337" : "#2E7D32",
                      fontWeight: 600,
                      marginLeft: "4px",
                    }}
                  >
                    {textareaContent.trim() ? tokenCount : 0}
                  </span>
                </TextView>
              </TokensHeadingContainer>
            </TokensContainer>

            {textareaContent.trim() && tokenCount > availableTokens && (
              <ButtonContainer>
                <Button
                  variant={"outlined"}
                  onClick={() => setOpenPurchaseDialog(true)}
                >
                  Buy More
                </Button>
                <UpgradeText
                  onClick={() => {
                    openSubscriptionPopup();
                  }}
                >
                  Upgrade Plan
                </UpgradeText>
              </ButtonContainer>
            )}
          </TokenAvailabilityContainer>

          <div
            style={{
              padding: "16px",
              backgroundColor: colors.PrimaryPurple100,
              borderRadius: "8px",
              margin: "16px 0",

              border: " 1px solid  #F79A00",
              backgroundColor: "#FCEFD8",
            }}
          >
            <TextView variant="largeText">
              <b>Disclaimer:</b> The AI Wheel Builder provides a starting point
              with images and videos that may not fully match your prompt, but
              are intended to inspire your Wheel creation.
            </TextView>
          </div>

          <Footer>
            <div>
              <button className="reset" onClick={() => setTextareaContent("")}>
                <TextView variant="largeText" color="#0F52BA">
                  Reset / Clear
                </TextView>
              </button>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant={"secondary"} onClick={handleClose}>
                Previous
              </Button>
              <button
                style={{
                  backgroundColor:
                    (!isWithinLimit || textareaContent === "") && "#EFE9FE",
                  cursor: textareaContent === "" && "not-allowed",
                }}
                className="generate"
                onClick={handleUpload}
                disabled={!isWithinLimit}
              >
                <TextView
                  variant="largeText"
                  color={textareaContent === "" ? "#BBBBBB" : "white"}
                >
                  Generate
                </TextView>
              </button>
              {loading && <Loader />}
            </div>
          </Footer>
        </ModalContainer>
      </Modal>

      {openPurchaseDialog && (
        <StyledDialog
          maxWidth={"lg"}
          open={openPurchaseDialog}
          onClose={closePurchaseDialog}
        >
          <StyledDialogContent>
            <StyledDialogTitle>
              <TextView variant="title">AI Token bundles</TextView>
            </StyledDialogTitle>
            <Content>
              <TokenUpgradeContent />
            </Content>

            <DialogButtonContainer>
              <Button variant="secondary" onClick={closePurchaseDialog}>
                Cancel
              </Button>
              <Button
                onClick={openPurchaseConfirmationPopup}
                disabled={selectedBundle !== null ? false : true}
              >
                Purchase
              </Button>
            </DialogButtonContainer>
          </StyledDialogContent>
        </StyledDialog>
      )}
    </>
  );
}

export default NewAIBuilder;
